import { PhysioLayout } from "@/components/layout/physio-layout";
import { useState, useEffect } from "react";
import { Award, ExternalLink, GraduationCap, Plus, Trash2, ChevronDown, ChevronUp, Loader2, X, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";

const ACPR_RESOURCES = [
  {
    title: "ACPR Membership Information",
    description: "The Association of Chartered Physiotherapists in Rheumatology (ACPR) is the specialist clinical interest group for rheumatology physiotherapists within the CSP.",
    links: [
      { label: "Become an ACPR member", url: "https://www.csp.org.uk/networks/physiotherapy-networks/association-chartered-physiotherapists-rheumatology" },
    ],
  },
  {
    title: "Clinical Standards & Position Statements",
    description: "ACPR publishes position statements on key clinical areas including exercise in inflammatory arthritis, hydrotherapy, and physiotherapy management of specific rheumatic conditions.",
    links: [
      { label: "ACPR clinical standards (CSP)", url: "https://www.csp.org.uk/networks/physiotherapy-networks/association-chartered-physiotherapists-rheumatology" },
      { label: "BSR physiotherapy guidance", url: "https://www.rheumatology.org.uk/practice-quality/guidelines" },
    ],
  },
  {
    title: "ACPR CPD Resources",
    description: "ACPR provides specialist CPD resources, study days, and an annual conference for rheumatology physiotherapists.",
    links: [
      { label: "ACPR study days & events", url: "https://www.csp.org.uk/networks/physiotherapy-networks/association-chartered-physiotherapists-rheumatology" },
    ],
  },
  {
    title: "Key Position Statements",
    description: "Evidence-based position statements covering exercise prescription, hydrotherapy, and the physiotherapy management of inflammatory arthritis, OA, and connective tissue diseases.",
    links: [],
    bullets: [
      "Exercise in Inflammatory Arthritis (ACPR / EULAR aligned)",
      "Hydrotherapy in Rheumatic Diseases",
      "Physiotherapy in Ankylosing Spondylitis / axSpA",
      "Assessment and Outcome Measures in Rheumatology",
      "Physiotherapy Management of Systemic Lupus Erythematosus",
    ],
  },
];

interface CpdActivity {
  id: number;
  title: string;
  date: string;
  category: string;
  hours: number;
  learningNeed?: string;
  activityUndertaken?: string;
  createdAt: string;
}

export default function AcprSpecialistSection() {
  const [activities, setActivities] = useState<CpdActivity[]>([]);
  const [showForm, setShowForm] = useState(false);
  const [openSection, setOpenSection] = useState<number | null>(0);
  const [title, setTitle] = useState("");
  const [date, setDate] = useState(new Date().toISOString().split("T")[0]);
  const [hours, setHours] = useState("");
  const [learningNeed, setLearningNeed] = useState("ACPR specialist development");
  const [activityUndertaken, setActivityUndertaken] = useState("");
  const [outcome, setOutcome] = useState("");
  const [patientImpact, setPatientImpact] = useState("");
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState<number | null>(null);

  const load = async () => {
    const res = await fetch(`/api/physio/cpd?year=${new Date().getFullYear()}`);
    if (res.ok) {
      const data: CpdActivity[] = await res.json();
      setActivities(data.filter(a => a.learningNeed?.toLowerCase().includes("acpr") || a.activityUndertaken?.toLowerCase().includes("acpr")));
    }
  };

  useEffect(() => { load(); }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    try {
      const res = await fetch("/api/physio/cpd", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          title: title.trim(),
          date,
          category: "professional",
          hours: parseFloat(hours),
          learningNeed: learningNeed.trim(),
          activityUndertaken: activityUndertaken.trim(),
          outcome: outcome.trim(),
          patientImpact: patientImpact.trim(),
        }),
      });
      if (res.ok) { setShowForm(false); load(); }
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm("Delete this activity?")) return;
    setDeletingId(id);
    try {
      await fetch(`/api/physio/cpd/${id}`, { method: "DELETE" });
      setActivities(prev => prev.filter(a => a.id !== id));
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <PhysioLayout title="ACPR Specialist Section">
      <div className="space-y-6">
        <div className="bg-gradient-to-br from-emerald-800 to-emerald-950 rounded-2xl p-6 text-white">
          <div className="flex items-center gap-3 mb-3">
            <Award className="h-8 w-8 text-emerald-300" />
            <div>
              <h2 className="text-xl font-bold">ACPR Specialist Hub</h2>
              <p className="text-emerald-300 text-sm">Association of Chartered Physiotherapists in Rheumatology</p>
            </div>
          </div>
          <p className="text-emerald-200 text-sm leading-relaxed">
            The ACPR is the specialist clinical interest group for rheumatology physiotherapists within the CSP. Membership supports specialist CPD, networking, and access to clinical standards and resources.
          </p>
        </div>

        <div className="space-y-3">
          {ACPR_RESOURCES.map((section, i) => (
            <div key={i} className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
              <button
                onClick={() => setOpenSection(openSection === i ? null : i)}
                className="w-full flex items-center justify-between p-5 text-left hover:bg-slate-50 transition-colors"
              >
                <span className="font-semibold text-slate-900">{section.title}</span>
                {openSection === i ? <ChevronUp className="h-4 w-4 text-slate-400" /> : <ChevronDown className="h-4 w-4 text-slate-400" />}
              </button>
              {openSection === i && (
                <div className="px-5 pb-5">
                  <p className="text-sm text-slate-600 mb-3 leading-relaxed">{section.description}</p>
                  {section.bullets && (
                    <ul className="space-y-1.5 mb-3">
                      {section.bullets.map((b) => (
                        <li key={b} className="flex items-start gap-2 text-sm text-slate-700">
                          <div className="w-1.5 h-1.5 rounded-full bg-emerald-500 mt-2 shrink-0" />
                          {b}
                        </li>
                      ))}
                    </ul>
                  )}
                  {section.links.map((link) => (
                    <a
                      key={link.url}
                      href={link.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-2 text-sm text-emerald-700 hover:text-emerald-800 font-medium"
                    >
                      <ExternalLink className="h-3.5 w-3.5" />
                      {link.label}
                    </a>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>

        <div className="bg-white rounded-2xl border border-slate-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <GraduationCap className="h-5 w-5 text-emerald-600" />
              <h3 className="font-bold text-slate-900">ACPR-Tagged CPD Activities</h3>
            </div>
            <Button onClick={() => setShowForm(true)} className="bg-emerald-700 hover:bg-emerald-800 text-white gap-2 text-sm">
              <Plus className="h-4 w-4" /> Log ACPR Activity
            </Button>
          </div>

          {showForm && (
            <form onSubmit={handleSubmit} className="bg-emerald-50 border border-emerald-200 rounded-xl p-4 mb-4 space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-sm font-semibold text-emerald-800">New ACPR CPD Activity</span>
                <button type="button" onClick={() => setShowForm(false)}><X className="h-4 w-4 text-slate-400" /></button>
              </div>
              <input value={title} onChange={e => setTitle(e.target.value)} placeholder="Activity title *" required className="w-full h-9 px-3 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500" />
              <div className="grid grid-cols-2 gap-3">
                <input type="date" value={date} onChange={e => setDate(e.target.value)} required className="h-9 px-3 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500" />
                <input type="number" min="0.5" step="0.5" value={hours} onChange={e => setHours(e.target.value)} placeholder="Hours *" required className="h-9 px-3 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500" />
              </div>
              <textarea value={activityUndertaken} onChange={e => setActivityUndertaken(e.target.value)} placeholder="Activity undertaken *" required rows={2} className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 resize-none" />
              <textarea value={outcome} onChange={e => setOutcome(e.target.value)} placeholder="Outcome / impact on practice *" required rows={2} className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 resize-none" />
              <textarea value={patientImpact} onChange={e => setPatientImpact(e.target.value)} placeholder="Patient / service user benefit *" required rows={2} className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 resize-none" />
              <div className="flex gap-2">
                <Button type="submit" disabled={saving} className="bg-emerald-700 hover:bg-emerald-800 text-white gap-2 text-sm">
                  {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />}
                  {saving ? "Saving..." : "Save"}
                </Button>
                <Button type="button" variant="outline" onClick={() => setShowForm(false)} className="text-sm">Cancel</Button>
              </div>
            </form>
          )}

          {activities.length === 0 ? (
            <p className="text-sm text-slate-500 text-center py-6">No ACPR-tagged activities logged yet. Activities with "ACPR" in the learning need or activity field will appear here.</p>
          ) : (
            <div className="space-y-2">
              {activities.map((a) => (
                <div key={a.id} className="flex items-center gap-4 p-3 bg-slate-50 rounded-xl">
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-slate-800">{a.title}</p>
                    <p className="text-xs text-slate-500">{new Date(a.date).toLocaleDateString("en-GB")} · {a.hours} hrs</p>
                  </div>
                  <button onClick={() => handleDelete(a.id)} disabled={deletingId === a.id} className="text-slate-400 hover:text-red-500 transition-colors">
                    {deletingId === a.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </PhysioLayout>
  );
}
